import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, XIcon, PrinterIcon, BookOpenIcon, ReceiptIcon, FileTextIcon, GearIcon,
  CaretLeftIcon, CaretRightIcon, ScalesIcon,
  WarningIcon
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";

// Descarga/abre un archivo autenticado (PDF en pestaña, Excel como descarga)
async function fetchAuthFile(path, filename) {
  try {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const headers = { Authorization: `Bearer ${getToken()}` };
    const cid = localStorage.getItem("agroport_company_id");
    if (cid) headers["X-Company-Id"] = cid;
    const res = await fetch(`${apiBase}${path}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    if (filename) {
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    } else window.open(url, "_blank");
  } catch (e) { toast.error(`Error: ${e.message}`); }
}

export default function SARCompliance() {
  const [tab, setTab] = useState("summary");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [retentions, setRetentions] = useState([]);
  const [params, setParams] = useState(null);
  const [openCert, setOpenCert] = useState(false);
  const [openParams, setOpenParams] = useState(false);
  const [openPrintBook, setOpenPrintBook] = useState(null);

  const loadAll = async () => {
    try {
      const [s, sb, pb, rt, fp] = await Promise.all([
        api.get(`/sar/summary?year=${year}&month=${month}`),
        api.get(`/sar/sales-book?year=${year}&month=${month}`),
        api.get(`/sar/purchases-book?year=${year}&month=${month}`),
        api.get(`/sar/retention-certificates?year=${year}&month=${month}`),
        api.get(`/sar/fiscal-params`),
      ]);
      setSummary(s.data); setSales(sb.data); setPurchases(pb.data);
      setRetentions(rt.data); setParams(fp.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [year, month]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  return (
    <Layout
      title="Cumplimiento Fiscal SAR"
      subtitle="PRD 4.7 · Honduras · Libro de Compras/Ventas, Retenciones, Parámetros Fiscales"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setOpenParams(true)} data-testid="sar-params-btn" className="bg-app border border-border hover:border-agro-green text-text-primary rounded-md px-3 py-2 text-xs flex items-center gap-1.5">
            <GearIcon size={12} /> Parámetros
          </button>
          <button onClick={() => setOpenCert(true)} data-testid="new-retention-btn" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" /> Nueva constancia
          </button>
        </div>
      }
    >
      {/* Period selector */}
      <div className="bg-card border border-border/60 rounded-lg p-4 mb-5 flex items-center justify-between" data-testid="sar-period">
        <div className="flex items-center gap-3">
          <button aria-label="Anterior" onClick={prevMonth} data-testid="prev-month" className="p-2 border border-border rounded-md text-text-muted hover:text-text-primary"><CaretLeftIcon size={14} /></button>
          <div className="text-center min-w-[200px]">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Período fiscal</div>
            <h3 className="font-heading text-lg font-semibold first-letter:uppercase">
              {new Date(year, month - 1).toLocaleDateString("es-HN", { month: "long", year: "numeric" })}
            </h3>
          </div>
          <button aria-label="Siguiente" onClick={nextMonth} data-testid="next-month" className="p-2 border border-border rounded-md text-text-muted hover:text-text-primary"><CaretRightIcon size={14} /></button>
        </div>
        {summary && (
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">ISV Neto</div>
              <div className={`font-heading text-xl font-semibold ${summary.isv_neto >= 0 ? "text-agro-green" : "text-red-400"}`} data-testid="sar-isv-neto">
                {fmtL(summary.isv_neto)}
              </div>
              <div className="text-[10px] text-text-muted">
                {summary.isv_neto >= 0 ? "a pagar al SAR" : "a favor"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-5 flex gap-1 overflow-x-auto">
        {[
          { k: "summary", l: "Resumen", i: ScalesIcon },
          { k: "sales", l: "Libro de Ventas", i: BookOpenIcon },
          { k: "purchases", l: "Libro de Compras", i: ReceiptIcon },
          { k: "retentions", l: "Constancias de Retención", i: FileTextIcon },
        ].map((t) => (
          <button
            key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-sar-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative flex items-center gap-2 whitespace-nowrap ${
              tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.i size={14} weight="duotone" /> {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "summary" && summary && <SummaryView summary={summary} />}
      {tab === "sales" && sales && <BookView title="Libro de Ventas" book={sales} kind="sales" onPrint={() => setOpenPrintBook({ kind: "sales", book: sales })}
        onExport={() => fetchAuthFile(`/sar/sales-book/export?year=${year}&month=${month}`, `libro_ventas_${year}_${month}.xlsx`)} />}
      {tab === "purchases" && purchases && <BookView title="Libro de Compras" book={purchases} kind="purchases" onPrint={() => setOpenPrintBook({ kind: "purchases", book: purchases })}
        onExport={() => fetchAuthFile(`/sar/purchases-book/export?year=${year}&month=${month}`, `libro_compras_${year}_${month}.xlsx`)} />}
      {tab === "retentions" && <RetentionsView items={retentions} onAdded={loadAll} />}

      {openCert && <RetentionForm params={params} onClose={() => setOpenCert(false)} onSaved={() => { setOpenCert(false); loadAll(); }} />}
      {openParams && params && <ParamsForm params={params} onClose={() => setOpenParams(false)} onSaved={() => { setOpenParams(false); loadAll(); }} />}
      {openPrintBook && <PrintBookModal data={openPrintBook} year={year} month={month} onClose={() => setOpenPrintBook(null)} />}
    </Layout>
  );
}

function SummaryView({ summary }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KpiCard label="Ventas del mes" value={fmtL(summary.ventas.total)} sub={`${summary.facturas_ventas} facturas`} accent="green" />
        <KpiCard label="Compras del mes" value={fmtL(summary.compras.total)} sub={`${summary.facturas_compras} facturas`} accent="yellow" />
        <KpiCard label="ISV débito (ventas)" value={fmtL(summary.isv_debito)} accent="green" />
        <KpiCard label="ISV crédito (compras)" value={fmtL(summary.isv_credito)} accent="yellow" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className={`bg-card border ${summary.isv_neto >= 0 ? "border-red-500/40" : "border-agro-green/40"} rounded-lg p-6`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Determinación ISV mensual</div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">ISV cobrado (ventas)</span>
              <span className="font-medium text-agro-green">{fmtL(summary.isv_debito)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">(−) ISV pagado (compras)</span>
              <span className="font-medium text-agro-yellow">{fmtL(summary.isv_credito)}</span>
            </div>
            <div className="border-t border-border pt-2 mt-2 flex justify-between items-end">
              <span className="font-medium">{summary.isv_neto >= 0 ? "ISV a pagar al SAR" : "ISV a favor"}</span>
              <span className={`font-heading text-2xl font-semibold ${summary.isv_neto >= 0 ? "text-red-400" : "text-agro-green"}`}>
                {fmtL(Math.abs(summary.isv_neto))}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Retenciones del período</div>
          <div className="mt-4 space-y-2 text-sm">
            <RetRow label="Retenciones ISR emitidas (a proveedores)" v={summary.retenciones.emitidas_isr} accent="yellow" />
            <RetRow label="Retenciones ISV emitidas (a proveedores)" v={summary.retenciones.emitidas_isv} accent="yellow" />
            <RetRow label="Retenciones ISR recibidas (de clientes)" v={summary.retenciones.recibidas_isr} accent="green" />
            <RetRow label="Retenciones ISV recibidas (de clientes)" v={summary.retenciones.recibidas_isv} accent="green" />
          </div>
        </div>
      </div>

      <div className="bg-app border border-border/60 rounded-md p-4 text-xs text-text-muted flex items-start gap-3">
        <ReceiptIcon size={18} className="shrink-0 text-agro-yellow mt-0.5" weight="duotone" />
        <div>
          <p className="text-text-secondary"><strong>Recordatorio fiscal:</strong> el ISV debe declararse y pagarse al SAR dentro de los primeros 10 días hábiles del mes siguiente. Las retenciones del ISR e ISV se enteran en la misma declaración. Los libros de Compras y Ventas y las constancias de retención deben conservarse 5 años.</p>
          <p className="mt-2">Estas cifras son preliminares basadas en las facturas registradas en el ERP. <strong>Valídelas con su contador antes de la declaración oficial.</strong></p>
        </div>
      </div>
    </div>
  );
}

function RetRow({ label, v, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-text-secondary">{label}</span>
      <span className={`font-medium ${accent === "green" ? "text-agro-green" : "text-agro-yellow"}`}>{fmtL(v)}</span>
    </div>
  );
}

function KpiCard({ label, value, sub, accent }) {
  const map = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400" };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">{label}</div>
      <div className={`font-heading text-xl font-semibold mt-1 ${map[accent]}`}>{value}</div>
      {sub && <div className="text-[10px] text-text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function BookView({ title, book, kind, onPrint, onExport }) {
  const isSales = kind === "sales";
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold">{title}</h3>
          <div className="text-xs text-text-muted">{book.count} {book.count === 1 ? "factura" : "facturas"} · Total {fmtL(book.totales.total)} · ISV {fmtL(book.totales.isv)}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={onExport} data-testid={`export-book-${kind}`} className="border border-agro-green/40 text-agro-green rounded-md px-3 py-1.5 text-xs">Exportar Excel</button>
          <button onClick={onPrint} data-testid={`print-book-${kind}`} className="bg-agro-yellow hover:opacity-90 text-black font-medium rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
            <PrinterIcon size={12} weight="bold" /> Imprimir libro
          </button>
        </div>
      </div>
      {book.rows.length === 0 ? (
        <div className="p-10 text-center text-text-muted text-sm">Sin facturas en este período</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-sidebar">
              <tr className="text-left uppercase tracking-wider text-[10px] text-text-muted">
                <th className="p-2.5 pl-5">Fecha</th>
                <th className="p-2.5">N° factura</th>
                <th className="p-2.5">CAI</th>
                <th className="p-2.5">RTN</th>
                <th className="p-2.5">{isSales ? "Cliente" : "Proveedor"}</th>
                <th className="p-2.5 text-right">Gravado 15%</th>
                <th className="p-2.5 text-right">Gravado 18%</th>
                <th className="p-2.5 text-right">Exento</th>
                <th className="p-2.5 text-right">ISV</th>
                <th className="p-2.5 text-right pr-5">Total</th>
              </tr>
            </thead>
            <tbody>
              {book.rows.map((r, i) => (
                <tr key={i} className="border-t border-border/30 hover:bg-white/[0.02]">
                  <td className="p-2.5 pl-5">{fmtDate(r.fecha)}</td>
                  <td className="p-2.5 font-mono text-agro-green">{isSales ? r.numero : r.numero_interno}</td>
                  <td className="p-2.5 font-mono text-[10px] text-text-muted">{isSales ? r.cai : r.cai_proveedor}</td>
                  <td className="p-2.5 font-mono text-[10px]">{isSales ? r.cliente_rtn : r.proveedor_rtn}</td>
                  <td className="p-2.5">{isSales ? r.cliente_nombre : r.proveedor_nombre}</td>
                  <td className="p-2.5 text-right">{fmtL(r.subtotal_gravado_15)}</td>
                  <td className="p-2.5 text-right">{r.subtotal_gravado_18 > 0 ? fmtL(r.subtotal_gravado_18) : "—"}</td>
                  <td className="p-2.5 text-right">{r.subtotal_exento > 0 ? fmtL(r.subtotal_exento) : "—"}</td>
                  <td className="p-2.5 text-right">{fmtL(r.isv)}</td>
                  <td className="p-2.5 text-right pr-5 font-medium">{fmtL(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-sidebar border-t-2 border-border">
              <tr>
                <td colSpan="5" className="p-3 pl-5 uppercase tracking-wider text-[10px] text-text-muted">Totales</td>
                <td className="p-3 text-right font-medium">{fmtL(book.totales.gravado_15)}</td>
                <td className="p-3 text-right font-medium">{fmtL(book.totales.gravado_18)}</td>
                <td className="p-3 text-right font-medium">{fmtL(book.totales.exento)}</td>
                <td className="p-3 text-right font-medium text-agro-yellow">{fmtL(book.totales.isv)}</td>
                <td className="p-3 text-right pr-5 font-heading text-base font-semibold text-agro-green">{fmtL(book.totales.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function RetentionsView({ items, onAdded }) {
  if (!items.length) {
    return (
      <div className="bg-card border border-border/60 rounded-lg p-10 text-center">
        <FileTextIcon size={32} className="text-text-muted mx-auto mb-3" weight="duotone" />
        <div className="text-text-secondary text-sm">Sin constancias de retención en este período</div>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">N°</th>
            <th className="p-3">Fecha</th>
            <th className="p-3">Tipo</th>
            <th className="p-3">Naturaleza</th>
            <th className="p-3">Contraparte</th>
            <th className="p-3">RTN</th>
            <th className="p-3 text-right">Base</th>
            <th className="p-3">%</th>
            <th className="p-3 text-right">Retenido</th>
            <th className="p-3 pr-5 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`cert-row-${r.numero}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-green">{r.numero}</td>
              <td className="p-3 text-xs">{fmtDate(r.fecha)}</td>
              <td className="p-3">
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${r.tipo === "emitida" ? "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}`}>
                  {r.tipo}
                </span>
              </td>
              <td className="p-3">
                <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30">{r.naturaleza}</span>
              </td>
              <td className="p-3 text-sm">{r.contraparte_nombre}</td>
              <td className="p-3 font-mono text-[10px]">{r.contraparte_rtn || "—"}</td>
              <td className="p-3 text-right text-sm">{fmtL(r.monto_base)}</td>
              <td className="p-3 text-xs text-text-secondary">{r.porcentaje}%</td>
              <td className="p-3 text-right text-sm font-medium text-agro-green">{fmtL(r.monto_retenido)}</td>
              <td className="p-3 pr-5 text-right whitespace-nowrap">
                <button onClick={() => fetchAuthFile(`/sar/retention-certificates/${r.id}/pdf`)} data-testid={`cert-pdf-${r.numero}`} className="text-agro-yellow text-xs hover:underline mr-3">PDF</button>
                <button onClick={async () => {
                  if (!window.confirm(`¿Anular constancia ${r.numero}? Se revierten asiento y saldo de factura.`)) return;
                  try { await api.post(`/sar/retention-certificates/${r.id}/void`); toast.success("Constancia anulada"); onAdded && onAdded(); }
                  catch (e) { toast.error(apiErr(e)); }
                }} data-testid={`cert-void-${r.numero}`} className="text-red-400 text-xs hover:underline">Anular</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetentionForm({ params, onClose, onSaved }) {
  const [form, setForm] = useState({
    tipo: "emitida", naturaleza: "ISR", contraparte_tipo: "proveedor",
    contraparte_nombre: "", contraparte_rtn: "",
    monto_base: 0,
    porcentaje: params?.isr_retencion_pct || 12.5,
    descripcion: "", factura_numero: "", cai: "", factura_id: "",
  });
  const [facturas, setFacturas] = useState([]);
  useEffect(() => {
    // Facturas con saldo para aplicar la retención: CxP (emitida) o CxC (recibida)
    const url = form.tipo === "emitida" ? "/payables" : "/receivables";
    api.get(url).then((r) => setFacturas(r.data || [])).catch(() => setFacturas([]));
    setForm((s) => ({ ...s, factura_id: "", contraparte_tipo: s.tipo === "emitida" ? "proveedor" : "cliente" }));
  }, [form.tipo]);
  const pickFactura = (id) => {
    const f = facturas.find((x) => x.id === id);
    if (!f) return setForm({ ...form, factura_id: "" });
    setForm({
      ...form, factura_id: id, factura_numero: f.numero_interno || f.numero || "",
      contraparte_id: f.proveedor_id || f.cliente_id || null,
      contraparte_nombre: f.proveedor_nombre || f.cliente_nombre || form.contraparte_nombre,
      contraparte_rtn: f.proveedor_rtn || f.cliente_rtn || form.contraparte_rtn,
    });
  };
  const monto_retenido = Number(form.monto_base || 0) * (Number(form.porcentaje || 0) / 100);

  useEffect(() => {
    // Cuando cambia naturaleza, ajustar porcentaje sugerido
    if (form.naturaleza === "ISR") setForm((s) => ({ ...s, porcentaje: params?.isr_retencion_pct || 12.5 }));
    else if (form.naturaleza === "ISV") setForm((s) => ({ ...s, porcentaje: params?.isv_retencion_pct || 0 }));
    // eslint-disable-next-line
  }, [form.naturaleza]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contraparte_nombre.trim()) return toast.error("Contraparte requerida");
    if (Number(form.monto_base) <= 0) return toast.error("Monto debe ser > 0");
    try {
      const { data } = await api.post("/sar/retention-certificates", {
        ...form,
        factura_id: form.factura_id || null,
        monto_base: Number(form.monto_base),
        porcentaje: Number(form.porcentaje),
      });
      toast.success(`${data.numero} emitida`);
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="retention-modal">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.7 · Constancia de retención</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">Nueva constancia</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo *</label>
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value, contraparte_tipo: e.target.value === "emitida" ? "proveedor" : "cliente" })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="emitida">Emitida (a proveedor)</option>
                <option value="recibida">Recibida (de cliente)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Naturaleza *</label>
              <select data-testid="rc-naturaleza" value={form.naturaleza} onChange={(e) => setForm({ ...form, naturaleza: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="ISR">ISR (Renta)</option>
                <option value="ISV">ISV (Sobre Ventas)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Contraparte (nombre) *</label>
              <input data-testid="rc-contraparte" value={form.contraparte_nombre} onChange={(e) => setForm({ ...form, contraparte_nombre: e.target.value })} required className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">RTN</label>
              <input value={form.contraparte_rtn} onChange={(e) => setForm({ ...form, contraparte_rtn: e.target.value })} placeholder="08019995123456" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Monto base (L.) *</label>
              <input data-testid="rc-monto" type="number" step="0.01" min="0" value={form.monto_base} onChange={(e) => setForm({ ...form, monto_base: e.target.value })} required className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">% Retención *</label>
              <input type="number" step="0.01" min="0" max="100" value={form.porcentaje} onChange={(e) => setForm({ ...form, porcentaje: e.target.value })} required className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Retenido</label>
              <div className="bg-agro-green/10 border border-agro-green/30 rounded-md px-3 py-2 text-sm font-semibold text-agro-green">
                {fmtL(monto_retenido)}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">
              Aplicar a factura con saldo ({form.tipo === "emitida" ? "CxP proveedor" : "CxC cliente"}) — opcional
            </label>
            <select value={form.factura_id} onChange={(e) => pickFactura(e.target.value)} data-testid="ret-factura"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— Sin factura (solo registrar) —</option>
              {facturas.map((f) => (
                <option key={f.id} value={f.id}>
                  {(f.numero_interno || f.numero)} · {(f.proveedor_nombre || f.cliente_nombre)} · saldo {fmtL(f.saldo_pendiente)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">N° factura referencia</label>
              <input value={form.factura_numero} onChange={(e) => setForm({ ...form, factura_numero: e.target.value })} placeholder="FE-001 / FP-001" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">CAI (si aplica)</label>
              <input value={form.cai} onChange={(e) => setForm({ ...form, cai: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Descripción</label>
            <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="ej. Retención sobre honorarios de mayo 2026" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving} type="submit" data-testid="save-rc-btn" className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm">Emitir constancia</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ParamsForm({ params, onClose, onSaved }) {
  const [form, setForm] = useState(params);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/sar/fiscal-params", {
        ...form,
        isv_pct_default: Number(form.isv_pct_default),
        isv_pct_extraordinario: Number(form.isv_pct_extraordinario),
        isr_retencion_pct: Number(form.isr_retencion_pct),
        isv_retencion_pct: Number(form.isv_retencion_pct),
      });
      toast.success("Parámetros fiscales actualizados");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fiscal-params-modal">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.7 · Parámetros fiscales</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">Configurar parámetros SAR</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="text-xs text-text-muted bg-agro-yellow/10 border border-agro-yellow/30 rounded-md p-3">
            <WarningIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Estos parámetros impactan toda la facturación y retenciones. <strong>Coordinar con el contador</strong> antes de cambiar.
          </div>

          <div className="space-y-3">
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-semibold">Impuesto sobre Ventas (ISV)</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="ISV estándar %" value={form.isv_pct_default} onChange={(v) => setForm({ ...form, isv_pct_default: v })} />
              <Field label="ISV extraordinario % (bebidas/telecom)" value={form.isv_pct_extraordinario} onChange={(v) => setForm({ ...form, isv_pct_extraordinario: v })} />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-semibold">Retenciones</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="ISR retención % (honorarios)" value={form.isr_retencion_pct} onChange={(v) => setForm({ ...form, isr_retencion_pct: v })} />
              <Field label="ISV retención % (no domiciliados)" value={form.isv_retencion_pct} onChange={(v) => setForm({ ...form, isv_retencion_pct: v })} />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-semibold">Datos del emisor</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RTN emisor" value={form.rtn_emisor} type="text" onChange={(v) => setForm({ ...form, rtn_emisor: v })} />
              <Field label="Razón social" value={form.razon_social_emisor} type="text" onChange={(v) => setForm({ ...form, razon_social_emisor: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Dirección fiscal" value={form.direccion_fiscal} type="text" onChange={(v) => setForm({ ...form, direccion_fiscal: v })} />
              <Field label="Teléfono" value={form.telefono_fiscal} type="text" onChange={(v) => setForm({ ...form, telefono_fiscal: v })} />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-semibold">Cuentas contables enlazadas</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="ISV Crédito Fiscal" value={form.cuenta_isv_credito_fiscal} type="text" onChange={(v) => setForm({ ...form, cuenta_isv_credito_fiscal: v })} />
              <Field label="ISV por Pagar" value={form.cuenta_isv_por_pagar} type="text" onChange={(v) => setForm({ ...form, cuenta_isv_por_pagar: v })} />
              <Field label="ISR retenido por pagar" value={form.cuenta_isr_retenido_por_pagar} type="text" onChange={(v) => setForm({ ...form, cuenta_isr_retenido_por_pagar: v })} />
              <Field label="ISV retenido por pagar" value={form.cuenta_isv_retenido_por_pagar} type="text" onChange={(v) => setForm({ ...form, cuenta_isv_retenido_por_pagar: v })} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving} type="submit" data-testid="save-params-btn" className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm">Guardar parámetros</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "number" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">{label}</label>
      <input
        type={type} step={type === "number" ? "0.01" : undefined}
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green ${type !== "number" ? "" : "font-mono"}`}
      />
    </div>
  );
}

function PrintBookModal({ data, year, month, onClose }) {
  const { book, kind } = data;
  const title = kind === "sales" ? "LIBRO DE VENTAS" : "LIBRO DE COMPRAS";
  const isSales = kind === "sales";
  const periodo = new Date(year, month - 1).toLocaleDateString("es-HN", { month: "long", year: "numeric" }).toUpperCase();

  const m = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 print:bg-white print:p-0" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[92vh] overflow-y-auto print:bg-white print:max-h-none print:max-w-none print:rounded-none print:border-0" onClick={(e) => e.stopPropagation()} data-testid="print-book-modal">
        <div className="p-4 border-b border-border flex items-center justify-between print:hidden">
          <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Vista de impresión · {title}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              <PrinterIcon size={12} weight="bold" /> Imprimir
            </button>
            <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
          </div>
        </div>
        <div className="p-6 voucher-printable bg-card print:bg-white print:text-black">
          <div className="text-center pb-3 border-b-2 border-agro-green print:border-black mb-4">
            <div className="font-heading text-xl font-bold text-agro-green print:text-black">AGROPORT</div>
            <div className="text-xs text-text-muted print:text-gray-700">ERP · Honduras</div>
            <div className="font-heading text-base mt-2 uppercase tracking-wider">{title}</div>
            <div className="text-xs text-text-muted print:text-gray-700">Período: {periodo}</div>
          </div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b-2 border-border print:border-black text-left">
                <th className="p-1.5">Fecha</th>
                <th className="p-1.5">N°</th>
                <th className="p-1.5">CAI</th>
                <th className="p-1.5">RTN</th>
                <th className="p-1.5">{isSales ? "Cliente" : "Proveedor"}</th>
                <th className="p-1.5 text-right">Gravado 15%</th>
                <th className="p-1.5 text-right">Exento</th>
                <th className="p-1.5 text-right">ISV</th>
                <th className="p-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {book.rows.map((r, i) => (
                <tr key={i} className="border-b border-border/30 print:border-gray-300">
                  <td className="p-1.5">{fmtDate(r.fecha)}</td>
                  <td className="p-1.5 font-mono">{isSales ? r.numero : r.numero_interno}</td>
                  <td className="p-1.5 font-mono text-[9px]">{isSales ? r.cai : r.cai_proveedor}</td>
                  <td className="p-1.5 font-mono text-[9px]">{isSales ? r.cliente_rtn : r.proveedor_rtn}</td>
                  <td className="p-1.5">{isSales ? r.cliente_nombre : r.proveedor_nombre}</td>
                  <td className="p-1.5 text-right">{fmtL(r.subtotal_gravado_15)}</td>
                  <td className="p-1.5 text-right">{r.subtotal_exento > 0 ? fmtL(r.subtotal_exento) : "—"}</td>
                  <td className="p-1.5 text-right">{fmtL(r.isv)}</td>
                  <td className="p-1.5 text-right font-medium">{fmtL(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border print:border-black font-semibold">
                <td colSpan="5" className="p-2 uppercase tracking-wider">Totales del período</td>
                <td className="p-2 text-right">{fmtL(book.totales.gravado_15)}</td>
                <td className="p-2 text-right">{fmtL(book.totales.exento)}</td>
                <td className="p-2 text-right">{fmtL(book.totales.isv)}</td>
                <td className="p-2 text-right">{fmtL(book.totales.total)}</td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-8 text-[10px] text-text-muted print:text-gray-600">
            <p>Documento emitido en cumplimiento del Art. 71 del Reglamento de la Ley del ISV. Conservar por 5 años. Sujeto a auditoría del SAR.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
